import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, Route, Routes } from 'react-router';
import { ElevatedButton } from '@producer/ui-kit';

import { Plus } from '../../../components/icons';
import { SettingScreenLayout } from '../../../features/settings/components/setting-screen-layout';
import { InviteMemberModal } from '../../../features/members/components/invite-member.modal';
import { SpaceMembersTable } from '../../../features/members/components/space-members-table';
import { SubscriptionStatus } from '../../../app/entities';
import { SubscriptionFailureDialog } from '../../../components/dialogs/subscription-failure-dialog';
import { uiStore } from '../../../core/stores/ui-store';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { useCurrentSpace } from '../../../hooks/use-current-space';

import styles from './members-settings.route.module.scss';
export const MembersSettingsRoute: React.FC = observer(() => {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isSubscriptionFailureModalOpen, setIsSubscriptionFailureModalOpen] = useState(false);
  const [hint, setHint] = useState('');
  const [isShowingLimitsModal, setIsShowingLimitsModal] = useState(false);
  const space = useCurrentSpace();

  useEffect(() => {
    uiStore.showBack = true;

    uiStore.currentLocation = 'space.settings';

    return () => {
      uiStore.currentLocation = '';
    };
  }, []);

  const handleClick = () => {
    if (!space.canInviteMember()) {
      /* TODO: Uncomment this when we have a way to track events on the client side

      api.post('/track', {
        type: 'event',
        eventName: 'Add user attempt',
      });
      */

      setIsShowingLimitsModal(true);
      return;
    }

    if (
      space.subscription?.subscriptionId &&
      !space.isSelfServing &&
      space.subscription.status === SubscriptionStatus.PAST_DUE
    ) {
      return setIsSubscriptionFailureModalOpen(true);
    }

    setIsInviteModalOpen(true);
  };

  useEffect(() => {
    if (space.subscription?.subscriptionId && !space.isSelfServing) {
      const limitReached = !space.canAddNewMember(1);
      const limit = space.seats?.limit || 1;
      const current = space.seats?.current || 1;

      if (limitReached) {
        return setHint(
          `All ${limit} seats are taken. To add a creator you first have to remove a creator or revoke an invitation. To request additional seats, please contact <a href="mailto:sales@the-producer.io">sales@the-producer.io</a>`,
        );
      }

      setHint(`${limit - current} seats of ${limit} available`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space.seats?.current]);

  return (
    <SettingScreenLayout
      title="User settings"
      actions={
        space.canAddNewMember(1)
          ? [
              <ElevatedButton
                key="invite"
                text="Invite creator"
                icon={<Plus />}
                onClick={handleClick}
              />,
            ]
          : []
      }
    >
      <div className={styles['container']}>
        {isShowingLimitsModal && (
          <LimitReachedModal type="creators" onCancel={() => setIsShowingLimitsModal(false)} />
        )}

        <p className={styles['description']} dangerouslySetInnerHTML={{ __html: hint }} />

        <div className={styles['table-wrapper']}>
          <Routes>
            <Route index element={<SpaceMembersTable space={space} />} />

            {/* The list used to be split across creators/guests/invitations tabs. */}
            <Route
              path="*"
              element={<Navigate to={`/space/${space._id}/settings/users`} replace />}
            />
          </Routes>
        </div>
      </div>

      {isInviteModalOpen && (
        <InviteMemberModal onCancel={() => setIsInviteModalOpen(false)} spaceId={space._id} />
      )}

      {isSubscriptionFailureModalOpen && (
        <SubscriptionFailureDialog
          onCancel={() => setIsSubscriptionFailureModalOpen(false)}
          reason="pastDue"
        />
      )}
    </SettingScreenLayout>
  );
});
